// Where the run is, as five real steps. The Built step's marker is the built
// ring itself, so the strip and the count can never disagree.

import React from 'react';
import { formatTimestamp } from '../utils/date-utils';
import StepStrip, { type StepItem, type StepState } from '../components/ui/StepStrip';
import type { WorkOrder } from './workOrdersApi';

type Step = StepItem;

const when = (ts: string | null) => (ts ? formatTimestamp(ts, 'MMM D') : '');
const byOn = (name: string | null | undefined, ts: string | null) =>
  [name, when(ts)].filter(Boolean).join(', ') || null;

/** Steps behind the run, and where it stopped if it did. */
function reachedSteps(o: WorkOrder): { done: number; stoppedAt: number | null } {
  const reached = !o.approved_at
    ? 1
    : !o.released_at
      ? 2
      : o.closed_at
        ? 5
        : o.completed_count >= o.quantity
          ? 4
          : 3;
  if (o.status === 'on_hold' || o.status === 'cancelled') return { done: reached, stoppedAt: Math.min(reached, 4) };
  return { done: reached, stoppedAt: null };
}

export function buildSteps(o: WorkOrder): Step[] {
  const { done, stoppedAt } = reachedSteps(o);
  const stateAt = (i: number): StepState => {
    if (i < done) return 'done';
    if (stoppedAt === i) return o.status === 'on_hold' ? 'held' : 'stopped';
    if (stoppedAt !== null) return 'future';
    return i === done ? 'current' : 'future';
  };
  const left = Math.max(0, o.quantity - o.completed_count);
  const fromSale = o.source === 'sales_order';

  const steps: Step[] = [
    {
      key: 'raised',
      label: fromSale ? 'Suggested' : 'Raised',
      state: stateAt(0),
      detail: fromSale ? (o.so_number ? `From ${o.so_number}` : 'From a confirmed sale') : `By hand, ${when(o.created_at)}`,
    },
    {
      key: 'approved',
      label: 'Approved',
      state: stateAt(1),
      detail: stateAt(1) === 'done' ? byOn(o.approved_by_name, o.approved_at) : stateAt(1) === 'current' ? 'Waiting for a manager' : null,
    },
    {
      key: 'released',
      label: 'Released',
      state: stateAt(2),
      detail:
        stateAt(2) === 'done' ? byOn(o.released_by_name, o.released_at) : stateAt(2) === 'current' ? 'Ready for the floor' : null,
    },
    {
      key: 'built',
      label: `Built ${o.completed_count} of ${o.quantity}`,
      progress: { value: o.completed_count, max: o.quantity },
      state: stateAt(3),
      detail: stateAt(3) === 'done' ? 'Every drone built' : stateAt(3) === 'current' ? `${left} to go` : null,
    },
    {
      key: 'closed',
      label: 'Closed',
      state: stateAt(4),
      detail: stateAt(4) === 'done' ? byOn(o.closed_by_name, o.closed_at) : stateAt(4) === 'current' ? 'Ready to close' : null,
    },
  ];
  if (stoppedAt !== null) {
    const word = o.status === 'on_hold' ? 'On hold' : o.approved_at ? 'Cancelled' : 'Dismissed';
    steps[stoppedAt] = {
      ...steps[stoppedAt],
      label: stoppedAt === 3 ? `${word}, ${o.completed_count} of ${o.quantity} built` : word,
      detail: o.status === 'cancelled' ? byOn(o.cancelled_by_name, o.cancelled_at) : null,
    };
  }
  return steps;
}

const JourneyStrip: React.FC<{ order: WorkOrder }> = ({ order }) => (
  <StepStrip label="Progress" steps={buildSteps(order)} />
);

export default JourneyStrip;
